<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import LanguageSelector from '@/components/LanguageSelector.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useRestaurantNav } from '@/composition/use-restaurant-nav';
  import { IBranch } from '@/interfaces/IBranch';
  import { IMember } from '@/interfaces/IMember';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { IUser } from '@/interfaces/IUser';
  import { clearWebViewCacheSafe } from '@/lib/capacitor/WebViewCache';
  import { Globals } from '@/modules/Globals';
  import { createQrBase64 } from '@/utils';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { getTopLevelDomain } from '@/utils/domain.utils';
  import V2ShellBranchPicker from './V2ShellBranchPicker.vue';
  import V2ShellDrawer from './V2ShellDrawer.vue';
  import V2ShellNav from './V2ShellNav.vue';

  /** Side of the storefront QR preview (px). */
  const QR_SIZE = 120;

  /**
   * The restaurant drawer (plan §4.2): branch, navigation, and at the bottom
   * the storefront QR, the language and, in the app, a reload button.
   */
  const props = defineProps<{
    restaurant: IRestaurant;
    user: IUser;
    role: IMember;
    branches: IBranch[];
    branch: IBranch | null;
  }>();

  const isCapacitor = Globals.isCapacitor;
  const domain = getTopLevelDomain();
  const qrUrl = ref<string | null>(null);

  const effectiveTier = computed(() => getEffectiveTier(props.restaurant));
  const enabledBranches = computed(() =>
    props.branches.filter((branch) => branch.active)
  );

  const groups = useRestaurantNav(() => ({
    restaurant: props.restaurant,
    role: props.role,
    isStaff: !!props.user.admin,
  }));

  watch(
    () => props.restaurant.id,
    async (restaurantId) => {
      qrUrl.value = null;
      const url = await createQrBase64(
        `https://bistri.fi/go/${restaurantId}`,
        QR_SIZE * 2
      );
      // The store may have changed while the QR was drawn.
      if (props.restaurant.id === restaurantId) qrUrl.value = url;
    },
    { immediate: true }
  );

  async function reload() {
    await clearWebViewCacheSafe();
    window.location.reload();
  }
</script>

<template>
  <V2ShellDrawer>
    <div
      v-if="effectiveTier > 1 && enabledBranches.length > 1"
      class="drawer-branch"
    >
      <V2ShellBranchPicker
        :branches="enabledBranches"
        :branch="branch"
        :prefix="$t('branch')"
        block
      />
    </div>

    <V2ShellNav
      :groups="groups"
      :effective-tier="effectiveTier"
      :label="$t('shell.nav')"
    />

    <template #footer>
      <a
        v-if="qrUrl"
        class="drawer-qr"
        :href="`https://${domain}/${restaurant.stringId}`"
        target="_blank"
        rel="noopener"
      >
        <img class="qr-image" :src="qrUrl" alt="" />
        <span class="qr-caption">{{ $t('qr.preview') }}</span>
      </a>
      <LanguageSelector sidebar />
      <div v-if="isCapacitor" class="drawer-reload">
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="refresh"
          @click="reload"
          >{{ $t('shell.refresh') }}</PosUiButton
        >
      </div>
    </template>
  </V2ShellDrawer>
</template>

<style scoped lang="scss">
  .drawer-branch {
    @apply tw-flex tw-px-3 tw-pt-3;
  }

  .drawer-qr {
    @apply tw-mx-auto tw-mb-2 tw-flex tw-w-max tw-flex-col tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-2 tw-text-neutral-500 tw-no-underline;

    &:hover {
      @apply tw-border-neutral-300;
    }

    .qr-image {
      @apply tw-block tw-h-[120px] tw-w-[120px];
    }

    .qr-caption {
      @apply tw-mt-1 tw-text-xs;
    }
  }

  .drawer-reload {
    @apply tw-flex tw-justify-center tw-pt-1;
  }
</style>
